/* eslint-disable react/no-unescaped-entities */

import React, { useEffect, useReducer, useRef, useState } from 'react';
import ReactDOMServer from 'react-dom/server';
import PropTypes from 'prop-types';
import api from '@utils/axios';

import I18n, { translate } from '@utils/i18n';
import { isProduction } from '@utils/environment';

import CommonSelect from '@components/Kendo/CommonSelect';
import BooleanSelect from '@components/Kendo/BooleanSelect';
import DateRangePicker from '@components/Kendo/DateRangePicker';
// import PagedMultiSelect from '@components/FilterPanel/nested/PagedMultiSelect';

import { DEFAULT_PAGE_SIZE, pageSizes, buildGridSettings } from '@constants/Kendo/gridSettings';
import toast from 'react-hot-toast';
import { hasGlobalTerritoriesApplied } from '@helpers/territories';
import { actionTypes, reducer, buildInitialFiltersState, isDateRangeStateField } from './IndexTableReducer';
import restoreGridPage from '../../../helpers/restoreGridPage';
import CustomScrollbar from '../../../components/CustomScrollbar/CustomScrollbar';
import GenericMultiSelect from '../../../components/Multiselects/GenericMultiSelect/GenericMultiSelect.tsx';
import TreeMultiSelect from '../../../components/Multiselects/TreeMultiSelect/TreeMultiSelect.tsx';
import SendReportEmailModal from '../SendReportEmailModal/index.tsx';
import './IndexTableStyles.scss';

// Add JQuery
const { kendo } = window;
const { $, confirm, ConfirmationModal } = window;

const DEFAULT_NAMESPACE = 'reports.datatable';
const DEFAULT_CACHE_KEY = 'reports/index';
const DEFAULT_NON_HIDABLE_COLUMNS = ['selectReports', 'contents', 'actions'];

// TODO: re-visit and see how we could minify the relationship between sprockets and webpacker part.
// Ideally, we should not mix them. But to be eliminate the waiting time for the duration
// of whole project being re-written and to be able to use React now - it's worth it.

function IndexTable({
  namespace,
  reportsUrl,
  deleteReportsUrl,
  markReportsPublicUrl,
  buildingsForSelect,
  inspectionTypesForSelect,
  techniciansForSelect,
  buildingStatusesForSelect,
  territoriesForSelect,
  assetsForSelect,
  hasShowAllReportsInCustomerPortal,
  canDeleteReports,
  cacheKey,
  defaultHiddenColumns,
  nonHidableColumns,
  hasShowInspectionDateFilter,
  hasShowIsPublicReportFilter,
  hasDisableAllFilters,
  hasDisableAllBulkActions,
  hasIntegrationStatus,
  canSendEmailInApp,
  pageSize
}) {
  const kendoCacheKey = `kendo/${cacheKey}`;
  const containerRef = useRef();
  const [emailReportId, setEmailReportId] = useState(null);
  const [sendEmailClickEvent] = useState(() => `click.sendReportEmail${kendo.guid()}`);
  const tableRef = useRef();
  const [state, dispatch] = useReducer(reducer, {
    tableFingerprint: undefined,
    reports: [],
    filters: buildInitialFiltersState()
  });

  // Mechanism to make state being up-to-date in Kendo grid callbacks.
  // Src: https://stackoverflow.com/a/60643670/20974169
  const filtersRef = useRef();
  filtersRef.current = state.filters;

  const tableFingerprintRef = useRef();
  tableFingerprintRef.current = state.tableFingerprint;

  const isMounted = !!state.tableFingerprint;
  const isMountedForCallbacks = () => !!tableFingerprintRef.current;
  let cachedPage = 1;

  useEffect(() => {
    setupTable();
    setupTableControls();
    loadCacheFromLocalStorage();

    dispatch({ type: actionTypes.TABLE_RELOADED });

    return () => $(document).off(sendEmailClickEvent);
  }, []);

  useEffect(() => {
    if (!isMounted) return;

    reloadTable();
    reloadTableControls();
  }, [state.tableFingerprint]);

  // ===================== JS Part ================================

  const setupTable = () => {
    const gridSettings = buildGridSettings(tableRef.current, {
      columns: [
        {
          field: 'selectReports',
          title: translate('table.headers.select_reports', { namespace }),
          sortable: false,
          columnMenu: false,
          headerTemplate: checkBoxHeaderTemplate,
          template: checkBoxTemplate,
          hidden: defaultHiddenColumns.includes('selectReports'), // to hide column by default
          menu: !nonHidableColumns.includes('selectReports'), // to show column in menu
          attributes: {
            class: 'table__cell--selectable'
          }
        },
        {
          field: 'contents',
          title: translate('table.headers.report', { namespace }),
          hidden: defaultHiddenColumns.includes('contents'),
          menu: !nonHidableColumns.includes('contents'),
          width: 150,
          template: contentsTemplate,
          sortable: false
        },
        {
          field: 'inspectionDate',
          title: translate('table.headers.inspection_date', { namespace }),
          template: inspectionDateTemplate,
          hidden: defaultHiddenColumns.includes('inspectionDate'),
          menu: !nonHidableColumns.includes('inspectionDate')
        },
        {
          field: 'account',
          title: I18n.t('generic.account'),
          template: accountTemplate,
          hidden: defaultHiddenColumns.includes('account'),
          menu: !nonHidableColumns.includes('account')
        },
        {
          field: 'building',
          title: I18n.t('generic.building'),
          template: buildingTemplate,
          hidden: defaultHiddenColumns.includes('building'),
          menu: !nonHidableColumns.includes('building')
        },
        {
          field: 'systemAsset',
          title: translate('table.headers.system_asset', { namespace }),
          sortable: false,
          template: `<span style="white-space: normal; word-break: break-all">#= (systemAsset) ? systemAsset : '-' #</span>`,
          hidden: defaultHiddenColumns.includes('systemAsset'),
          menu: !nonHidableColumns.includes('systemAsset')
        },
        {
          field: 'reportType',
          title: I18n.t('generic.report_type'),
          template: reportTypeTemplate,
          hidden: defaultHiddenColumns.includes('reportType'),
          menu: !nonHidableColumns.includes('reportType')
        },
        {
          field: 'displayName',
          title: translate('table.headers.display_name', { namespace }),
          template: `#= (displayName) ? displayName : '-' #`,
          hidden: defaultHiddenColumns.includes('displayName'),
          menu: !nonHidableColumns.includes('displayName')
        },
        {
          field: 'technicianName',
          title: I18n.t('generic.technician'),
          // `#:` HTML-encodes — a technician name is free text an office user types.
          template: `#: (technicianName) ? technicianName : '-' #`,
          hidden: defaultHiddenColumns.includes('technicianName'),
          menu: !nonHidableColumns.includes('technicianName')
        },
        {
          field: 'isPublicReport',
          title: translate('table.headers.is_public_report', { namespace }),
          hidden: defaultHiddenColumns.includes('isPublicReport'),
          menu: !nonHidableColumns.includes('isPublicReport')
        },
        {
          field: 'integrationStatus',
          title: translate('table.headers.integration_status', { namespace }),
          template:
                  "#= integrationStatus # #= (integrationStatus == 'Attached' && integrationDate) ? '(' + integrationDate + ')' + (externalId ? ' (' + externalId + ')' : '') : '' #",
          hidden: !hasIntegrationStatus,
          menu: hasIntegrationStatus
        },
        {
          field: 'sentOn',
          title: translate('table.headers.sent_on', { namespace }),
          template: "#= (sentOn) ? sentOn : '-' #",
          hidden: defaultHiddenColumns.includes('sentOn'),
          menu: !nonHidableColumns.includes('sentOn')
        },
        {
          field: 'actions',
          title: ' ',
          sortable: false,
          columnMenu: false,
          template: actionsTemplate,
          hidden: defaultHiddenColumns.includes('actions'),
          menu: !nonHidableColumns.includes('actions'),
          attributes: {
            class: 'table__cell--options--col-actions'
          }
        }
      ],
      pageable: {
        input: true,
        numeric: false,
        pageSize,
        pageSizes: pageSizes(pageSize)
      },
      dataSource: {
        schema: {
          model: {
            fields: {
              id: { type: 'number' },
              inspectionDate: { type: 'string' },
              createdAt: { type: 'string' },
              building: { type: 'string' },
              systemAsset: { type: 'string' },
              displayName: { type: 'string' },
              isPublicReport: { type: 'string' },
              hasPdfPresent: { type: 'boolean' },
              sentOn: { type: 'string' },
              account: { type: 'string' },
              skippedAttachmentsWarning: { type: 'string' }
            }
          }
        },
        sort: { field: 'createdAt', dir: 'desc' }
      },
      scrollable: false,
      columnShow: saveCacheToLocalStorage,
      columnHide: saveCacheToLocalStorage,
      dataBinding: () => {
        $('.rerender-inspection-reports-popup').remove();
      },
      dataBound: () => {
        new ConfirmationModal().init();
        saveCacheToLocalStorage();
        // eslint-disable-next-line no-undef
        loadDropdowns({ gridSelector: '#inspection-reports-grid', email_reports_callback: onSendEmail });

        restoreGridPage(cachedPage, $(tableRef.current).data('kendoGrid').dataSource);
        const pager = $(tableRef.current).data('kendoGrid').pager.element;
        pager.on('change', 'input', (e) => {
          const page = parseInt($(e.target).val(), 10);
          $(tableRef.current).data('kendoGrid').dataSource.page(page);
        });
      },
      sort: (e) => {
        if (!e.sort.dir) {
          e.preventDefault();
          e.sender.dataSource.sort({ field: 'createdAt', dir: 'desc' });
        }
      }
    });
    $(tableRef.current).kendoGrid(gridSettings);
  };

  const checkBoxTemplate = ReactDOMServer.renderToString(
    <div className="--checkbox">
      <input
        type="checkbox"
        data-download-link="#= downloadLink #"
        data-report-id="#= id #"
        data-can-delete="#= canDelete #"
        data-js-bulk-single-checkbox
      />
    </div>
  );
  const contentsTemplate = ReactDOMServer.renderToString(
    <>
      {'# if (hasPdfPresent) { #'}
      <a href="#= pdfLink #" title={I18n.t('generic.view')} target="blank" rel="noopener noreferrer">
        <i className="fa-light fa-eye" /> <span>#= createdAt #</span>
      </a>
      {'# } else { #'}
      <i>
        <span className="grey">#= reportStatus #</span>
      </i>
      {'# } #'}
      {'# if (skippedAttachmentsWarning) { # '}
      <i
        className="fa-solid fa-triangle-exclamation reports-listing__skipped-attachments"
        title="#: skippedAttachmentsWarning #"
      />
      {'# } #'}
    </>
  );
  const checkBoxHeaderTemplate = ReactDOMServer.renderToString(
    <input type="checkbox" className="--checkbox" data-js-bulk-all-checkbox />
  );
  const inspectionDateTemplate = ReactDOMServer.renderToString(
    <>
      {'# if (inspectionDate) { #'}
      {'# if (inspection == window.location.pathname) { #'}
      <span>#= inspectionDate #</span>
      {'# } else { #'}
      <u>
        <a href="#= inspection #">#= inspectionDate #</a>
      </u>
      {'# } #'}
      {'# } else { #'}
      <span>-</span>
      {'# } #'}
    </>
  );
  const accountTemplate = ReactDOMServer.renderToString(
    <>
      {'# if (account) { #'}
      {'# if (account == window.location.pathname) { #'}
      <span>#= accountName #</span>
      {'# } else { #'}
      <u>
        <a href="#= account #">#= accountName #</a>
      </u>
      {'# } #'}
      {'# } else { #'}
      <span>-</span>
      {'# } #'}
    </>
  );
  const buildingTemplate = ReactDOMServer.renderToString(
    <>
      {'# if (building) { #'}
      {'# if (building == window.location.pathname) { #'}
      <span>#= buildingName #</span>
      {'# } else { #'}
      <u>
        <a href="#= building #">#= buildingName #</a>
      </u>
      {'# } #'}
      {'# } else { #'}
      <span>-</span>
      {'# } #'}
    </>
  );

  const reportTypeTemplate = ReactDOMServer.renderToString(
    <>
      {'# if (reportType) { #'}
      {'# if (inspection == window.location.pathname) { #'}
      <span>#= reportType #</span>
      {'# } else { #'}
      <u>
        <a href="#= inspection #">#= reportType #</a>
      </u>
      {'# } #'}
      {'# } else { #'}
      <span>-</span>
      {'# } #'}
    </>
  );
  const actionsTemplate = ReactDOMServer.renderToString(
    <div>
      <button
        id="actions-button-#= id #"
        aria-label="Actions"
        type="button"
        className="qmb-control--icon--row-actions --react">
        <i className="fa-regular fa-ellipsis" />
      </button>
      <div
        id="inspection_reports_popup_#= id #"
        className="qmb-popup--action-list single-reports-popup rerender-inspection-reports-popup">
        <ul role="presentation">
          {'# if (hasPdfPresent) { #'}
          <li>
            <a
              href="#= pdfLink #"
              title={I18n.t('generic.view')}
              className="qmb-control"
              target="blank"
              rel="noopener noreferrer">
              <i className="fa-light fa-eye" /> {I18n.t('generic.view')}
            </a>
          </li>

          <li>
            <a href="#= downloadLink #" title={I18n.t('generic.download')} className="qmb-control">
              <i className="fa-light fa-circle-down" /> {I18n.t('generic.download')}
            </a>
          </li>

          {'# if (canSendEmail) { #'}
          <li>
            <a
              href="\\#"
              title={translate('table.buttons.send_to_contacts', { namespace })}
              className="qmb-control"
              data-action-send-report-email
              data-id="#= id #">
              <i className="fa-light fa-envelope" /> {translate('table.buttons.send_to_contacts', { namespace })}
            </a>
          </li>
          {'# } else { #'}
          <li>
            <a
              href="mailto:#= encodeURIComponent(emails) #?body=#= encodeURIComponent(emailBody) #&subject=#= encodeURIComponent(emailSubject) #"
              title={translate('table.buttons.send_to_contacts', { namespace })}
              className="qmb-control"
              id="email-reports"
              data-id="#= id #"
              data-url="#= showLink #">
              <i className="fa-light fa-envelope" /> {translate('table.buttons.send_to_contacts', { namespace })}
            </a>
          </li>
          {'# } #'}

          {'# } #'}

          {'# if (showApproveButton) { #'}
          <li>
            <a
              href="\\#"
              title={translate('table.buttons.approve_report', { namespace })}
              className="qmb-control"
              data-action-approve
              data-work-order-number="#= workOrderNumber #"
              data-has-pdf-present="#= hasPdfPresent #"
              data-action-button-id="actions-button-#= id #"
              data-path="#= approveLink #">
              <i className="fa-light fa-paper-plane-top" /> {translate('table.buttons.approve_report', { namespace })}
            </a>
          </li>
          {'# } #'}

          {'# if (canEdit) { #'}
          <li>
            <a
              href="#= editLink #"
              title={I18n.t('generic.edit_details')}
              className="qmb-control"
              target="blank"
              rel="noopener noreferrer"
              data-remote="true">
              <i className="fa-light fa-pen-to-square" /> {I18n.t('generic.edit_details')}
            </a>
          </li>
          {'# } #'}

          {'# if (tceButtonEnabled) { #'}
          <li>
            <a
              href="\\#"
              type="button"
              title={I18n.t('reports.datatable.table.buttons.push_to_tce')}
              className="qmb-control tce-single-report-submission"
              data-id="#= id #:#= technicianId #">
              <i className="fa-light fa-arrow-right" />{' '}
              <span>{I18n.t('reports.datatable.table.buttons.push_to_tce')}</span>
            </a>
          </li>
          {'# } #'}

          {'# if (irolButtonEnabled) { #'}
          <li>
            <a
              href="\\#"
              type="button"
              title={I18n.t('reports.datatable.table.buttons.push_to_irol').replace("'", '`')}
              className="qmb-control irol-single-report-submission"
              data-id="#= id #:#= technicianId #">
              <i className="fa-light fa-arrow-right" />{' '}
              <span>{I18n.t('reports.datatable.table.buttons.push_to_irol').replace("'", '`')}</span>
            </a>
          </li>
          {'# } #'}

          {'# if (livsafeButtonEnabled) { #'}
          <li>
            <a
              href="\\#"
              type="button"
              title={I18n.t('reports.datatable.table.buttons.push_to_livsafe').replace("'", '`')}
              className="qmb-control livsafe-single-report-submission"
              data-id="#= id #:#= technicianId #">
              <i className="fa-light fa-arrow-right" />{' '}
              <span>{I18n.t('reports.datatable.table.buttons.push_to_livsafe').replace("'", '`')}</span>
            </a>
          </li>
          {'# } #'}

          {'# if (canDelete) { #'}
          <li>
            <a
              href="/"
              type="button"
              title={I18n.t('generic.delete')}
              className="qmb-control--delete"
              data-title={translate('table.confirm_delete', { namespace })}
              data-link="#= deleteLink #"
              data-link-method="delete"
              data-js-confirmation-modal-trigger
              data-needs-confirm>
              <i className="fa-light fa-trash-can" /> {I18n.t('generic.delete')}
            </a>
          </li>
          {'# } #'}
        </ul>
      </div>
    </div>
  );

  const saveCacheToLocalStorage = () => {
    if (!isMountedForCallbacks()) return;

    const grid = $(tableRef.current).data('kendoGrid');
    const cacheData = {};

    cacheData.hiddenColumns = grid.columns.filter((column) => column.hidden).map((column) => column.field);
    cacheData.stateFilters = filtersRef.current;
    // eslint-disable-next-line no-underscore-dangle
    cacheData.kendoSortSettings = grid.dataSource._sort;
    cacheData.page = grid.dataSource.page();

    localStorage.setItem(kendoCacheKey, kendo.stringify(cacheData));
  };

  const setupTableControls = () => {
    $(document).on('click', '[data-action-approve]', (e) => {
      document.getElementById(e.currentTarget.getAttribute('data-action-button-id')).click();
      const missingPdf = e.currentTarget.getAttribute('data-has-pdf-present') === 'false';
      if (missingPdf) {
        toast.error(translate('table.report_not_generated', { namespace }));
        return;
      }
      const parsedWorkOrderNumber = Number.parseInt(e.currentTarget.getAttribute('data-work-order-number'), 10);
      if (Number.isNaN(parsedWorkOrderNumber) || parsedWorkOrderNumber <= 0) {
        toast.error(translate('table.work_order_required', { namespace }));
        return;
      }
      $('.rerender-inspection-reports-popup').remove();
      $('.k-animation-container').remove();
      onApproveReport(e);
    });

    $(document).on(sendEmailClickEvent, '[data-action-send-report-email]', (e) => {
      const reportId = parseInt(e.currentTarget.dataset.id, 10);
      if (!tableRef.current?.querySelector(`#actions-button-${reportId}`)) return;

      e.preventDefault();
      closeRowActionPopups();
      setEmailReportId(reportId);
    });

    $(tableRef.current).on('change', '[data-js-bulk-all-checkbox]', onSelectAllToggle);
    $(tableRef.current).on('click', '[data-js-bulk-single-checkbox]', onCheckboxToggle);
  };

  const updateBulkActionControls = () => {
    const $checked = $('[data-js-bulk-single-checkbox]:checked', tableRef.current);
    const anySelected = $checked.length > 0;

    // Default behavior: all bulk actions enabled if at least one selected
    $('.js-bulk-action', containerRef.current).attr('disabled', !anySelected);

    // Special rule for delete: disable if ANY selected item is not deletable (Attached)
    const anyNotDeletable =
      $checked.filter((_, el) => String(el.getAttribute('data-can-delete')).toLowerCase() !== 'true').length > 0;

    $('[data-js-bulk-delete]', containerRef.current).attr('disabled', !anySelected || anyNotDeletable);
  };

  const isBulkDeleteAllowed = () => {
    const $checked = $('[data-js-bulk-single-checkbox]:checked', tableRef.current);
    if ($checked.length === 0) return false;

    const anyNotDeletable =
      $checked.filter((_, el) => String(el.getAttribute('data-can-delete')).toLowerCase() !== 'true').length > 0;

    return !anyNotDeletable;
  };

  const onCheckboxToggle = (e) => {
    const checkbox = e.currentTarget;
    const reportId = checkbox.getAttribute('data-report-id');

    updateBulkActionControls();

    if (checkbox.checked) {
      dispatch({ type: actionTypes.REPORT_SELECTED, id: reportId });
    } else {
      $('[data-js-bulk-all-checkbox]', tableRef.current).prop('checked', false);
      dispatch({ type: actionTypes.REPORT_DESELECTED, id: reportId });
    }
  };

  const onSendEmail = (e) => {
    let today = new Date();
    const dd = String(today.getDate()).padStart(2, '0');
    const mm = String(today.getMonth() + 1).padStart(2, '0'); // January is 0!

    const yyyy = today.getFullYear();
    today = `${mm}/${dd}/${yyyy}`;

    const reportId = parseInt(e.currentTarget.dataset.id, 10);

    /* eslint-disable no-alert */
    api
      .patch(e.currentTarget.dataset.url, { report: { sent_on: today } }, { headers: { Accept: 'application/json' } })
      .then(() => {
        const grid = $(tableRef.current).data('kendoGrid');
        const { dataSource } = grid;
        const dataItem = dataSource.get(reportId);

        if (dataItem) {
          dataItem.set('sentOn', today);
        }
      })
      .catch(({ message }) => alert(`${I18n.t('generic.something_went_wrong')} - ${message}`));

    closeRowActionPopups();
  };

  /* Close any open row-action popup without destroying the Kendo widget */
  const closeRowActionPopups = () => {
    $('.rerender-inspection-reports-popup').each(function closePopup() {
      $(this).find(':focus').trigger('blur');
      const popup = $(this).data('kendoPopup');
      if (popup) popup.close();
    });
  };

  const onSelectAllToggle = (e) => {
    const $checkbox = $(e.currentTarget);

    if (e.currentTarget.checked) {
      $('[data-js-bulk-single-checkbox]', tableRef.current).prop('checked', true);
      dispatch({ type: actionTypes.REPORTS_DESELECTED });

      $('[data-js-bulk-single-checkbox]', tableRef.current).each((_index, row) => {
        dispatch({ type: actionTypes.REPORT_SELECTED, id: row.getAttribute('data-report-id') });
      });
    } else {
      $('[data-js-bulk-single-checkbox]', tableRef.current).prop('checked', false);
      dispatch({ type: actionTypes.REPORTS_DESELECTED });
    }

    updateBulkActionControls();
  };

  const onApproveReport = (e) => {
    api
      .post(e.currentTarget.getAttribute('data-path'), { headers: { Accept: 'application/json' } })
      .then(() => reloadTable())
      .catch(({ message }) => alert(`${I18n.t('generic.something_went_wrong')} - ${message}`));
  };

  const loadCacheFromLocalStorage = () => {
    if (!localStorage.getItem(kendoCacheKey)) return;

    try {
      const cacheData = JSON.parse(localStorage.getItem(kendoCacheKey));
      const grid = $(tableRef.current).data('kendoGrid');

      if (!hasIntegrationStatus && !cacheData.hiddenColumns?.includes('integrationStatus')) {
        cacheData.hiddenColumns.push('integrationStatus');
      }

      if (hasIntegrationStatus && cacheData.hiddenColumns?.includes('integrationStatus')) {
        cacheData.hiddenColumns = cacheData.hiddenColumns.filter((c) => c !== 'integrationStatus');
      }

      if (cacheData.hiddenColumns) {
        cacheData.hiddenColumns
          .filter((fieldName) => !nonHidableColumns.includes(fieldName))
          .forEach((fieldName) => grid.hideColumn(fieldName));

        grid.columns
          .map((column) => column.field)
          .filter((column) => !cacheData.hiddenColumns.includes(column))
          .forEach((fieldName) => grid.showColumn(fieldName));
      } else {
        grid.columns.map((column) => column.field).forEach((fieldName) => grid.showColumn(fieldName));
      }

      // eslint-disable-next-line no-underscore-dangle
      if (cacheData.kendoSortSettings) grid.dataSource._sort = cacheData.kendoSortSettings;
      if (cacheData.stateFilters) {
        dispatch({ type: actionTypes.LOAD_FILTERS_FROM_CACHE, filters: cacheData.stateFilters });
      }
      if (cacheData.page) {
        cachedPage = cacheData.page;
      }
    } catch {
      console.log(`Issue with Kendo grid cache: ${kendoCacheKey}`);
      localStorage.removeItem(kendoCacheKey);
    }
  };

  const reloadTable = () => {
    const filtersData = [];

    // Read from filtersRef instead of state.filters: reloadTable can be invoked from
    // callbacks bound once in the mount-only useEffect (e.g. onApproveReport's "Send to
    // Vista" success handler), whose closures capture the initial (empty) state.filters.
    // filtersRef.current is kept in sync with state.filters on every render, so it always
    // reflects the currently applied filters even from a stale closure.
    // eslint-disable-next-line no-restricted-syntax
    for (const [key, value] of Object.entries(filtersRef.current)) {
      if (!value) continue;
      // Date filters require a bit of different checks around them in different places.
      // If that part would be missed, it won't be critical, such as Back-End is prepared for empty values.
      //
      // This part is only about highlighting the grid header with red background
      // when inspectionDate field was used in filters.
      if (isDateRangeStateField(filtersRef.current[key]) && !value.startDate && !value.endDate) continue;

      filtersData.push({ field: key, operator: 'eq', value });
    }

    $(tableRef.current).data('kendoGrid').dataSource.filter(filtersData);
  };

  const reloadTableControls = () => {
    const $controls = $('.js-bulk-action', containerRef.current);
    $controls.attr('disabled', true);

    const $selectAll = $('[data-js-bulk-all-checkbox], [data-js-bulk-single-checkbox]', tableRef.current);
    $selectAll.prop('checked', false);

    updateBulkActionControls();
  };

  const onBulkDownload = (e) => {
    e.preventDefault();

    // eslint-disable-next-line no-alert
    if (!state.reports.length) return alert(translate('table.select_report', { namespace }));

    $('[data-js-bulk-single-checkbox]:checked', tableRef.current).each((_event, item) => {
      const url = item.getAttribute('data-download-link');

      if (url) window.open(url, '_blank', 'noopener,noreferrer');
    });
  };

  const onBulkPublish = (e) => {
    e.preventDefault();

    gatherReportsAndRedirect(markReportsPublicUrl, translate('table.confirm_bulk_publish', { namespace }));
  };

  const gatherReportsAndRedirect = (url, message) => {
    // eslint-disable-next-line no-alert
    if (!state.reports.length) return alert(translate('table.select_report', { namespace }));

    let queryParams = url.includes('building_id=') || url.includes('inspection_id=') ? '&' : '?';

    state.reports.forEach((reportId) => {
      queryParams = `${queryParams}ids[]=${reportId}&`;
    });
    queryParams = queryParams.slice(0, -1);

    const response = !message || confirm(message);
    if (response) {
      window.location.href = `${url}${queryParams}`;
    }
  };

  const onBulkDelete = (e) => {
    e.preventDefault();

    // Hard stop: div "disabled" doesn't block clicks, so we must guard here.
    if (!isBulkDeleteAllowed()) {
      toast.error('One or more selected reports are attached and cannot be deleted.');
      return;
    }

    gatherReportsAndRedirect(deleteReportsUrl, translate('table.confirm_bulk_delete', { namespace }));
  };

  const onChangeDateFilter = (fieldName) => {
    return (dateFieldName, value) => {
      dispatch({
        type: actionTypes.DATE_RANGE_FILTER_CHANGED,
        field: fieldName,
        dateField: dateFieldName,
        value
      });
      dispatch({ type: actionTypes.TABLE_RELOADED });
    };
  };

  const onChangeCommonFilter = (fieldName) => {
    return (e) => {
      dispatch({ type: actionTypes.FILTER_CHANGED, field: fieldName, value: e.target.value });
      dispatch({ type: actionTypes.TABLE_RELOADED });
    };
  };

  const handleMultiSelectFilterChange = (fieldName) => {
    return (value) => {
      dispatch({ type: actionTypes.FILTER_CHANGED, field: fieldName, value });
      dispatch({ type: actionTypes.TABLE_RELOADED });
    };
  };

  const onResetFilters = () => {
    dispatch({ type: actionTypes.FILTERS_RESET });
    dispatch({ type: actionTypes.TABLE_RELOADED });
  };

  // For more complicated scenarios, when you have issues with cached Kendo Grid,
  // to clear all Kendo Grid caches, use the next snippet:
  //
  // Object
  //   .keys(localStorage)
  //   .filter(key => key.startsWith('kendo/'))
  //   .forEach(key => {
  //     console.log("Removing item: ", key);
  //     localStorage.removeItem(key)
  //   });
  //
  const onResetCache = () => {
    if (!confirm(I18n.t('generic.are_you_sure'))) return;

    localStorage.removeItem(kendoCacheKey);
    window.location.reload();
  };

  // ===================== HTML Part ================================
  return (
    <div ref={containerRef}>
      {canSendEmailInApp && (
        <SendReportEmailModal reportId={emailReportId} onClose={() => setEmailReportId(null)} onSent={reloadTable} />
      )}
      {!hasDisableAllFilters && (
        <article id="filters" className="qmb-filters">
          <span className="filters__label">{translate('filters.title', { namespace })}</span>

          <fieldset className="filters__group">
            {hasShowInspectionDateFilter && (
              <DateRangePicker
                name="inspection_date"
                onAnyChange={onChangeDateFilter('inspectionDate')}
                values={{
                  rangeType: state.filters.inspectionDate.rangeType,
                  startDate: state.filters.inspectionDate.startDate,
                  endDate: state.filters.inspectionDate.endDate
                }}
                label={translate('filters.inspection_date', { namespace })}
                reactOptions={{ id: 'inspection-date' }}
              />
            )}

            {!!territoriesForSelect.length && !hasGlobalTerritoriesApplied() && (
              <div className="filters__option --active">
                <GenericMultiSelect
                  name="territory"
                  value={state.filters.territory || []}
                  dataUrl="/lookup?type=territory&with_unassigned=true"
                  valueMapUrl="/lookup/map?type=territory&with_unassigned=true"
                  elements={territoriesForSelect}
                  label={translate('filters.territory', { namespace })}
                  placeholder={`Search ${translate('plural_territory', { namespace: 'filters' })}`}
                  onChange={handleMultiSelectFilterChange('territory')}
                />
              </div>
            )}

            {!!buildingsForSelect.length && (
              <div className="filters__option --active">
                <GenericMultiSelect
                  name="building"
                  value={state.filters.building || []}
                  dataUrl="/lookup/buildings?type=building"
                  valueMapUrl="/lookup/map?type=building"
                  elements={buildingsForSelect}
                  label={translate('filters.building', { namespace })}
                  placeholder={`Search ${translate('plural_building', { namespace: 'filters' })}`}
                  onChange={handleMultiSelectFilterChange('building')}
                  template="
                  <div>#: label #</div>
                  <sub>#: address #</sub>
                  "
                />
              </div>
            )}

            {!!buildingStatusesForSelect.length && (
              <div className="filters__option--native --active">
                <CommonSelect
                  name="building_status"
                  value={state.filters.buildingStatus}
                  elements={buildingStatusesForSelect}
                  label={translate('filters.building_status', { namespace })}
                  placeholder={I18n.t('generic.all')}
                  selectOptions={{
                    id: 'building-status',
                    onChange: onChangeCommonFilter('buildingStatus')
                  }}
                />
              </div>
            )}

            <div className="filters__option --active">
              <GenericMultiSelect
                name="accounts"
                value={state.filters.account || []}
                dataUrl="/lookup?type=account"
                valueMapUrl="/lookup/map?type=account"
                label={translate('account', { namespace: 'filters' })}
                placeholder={`Search ${translate('plural_account', { namespace: 'filters' })}`}
                onChange={handleMultiSelectFilterChange('account')}
              />
            </div>

            {!!inspectionTypesForSelect.length && (
              <div className="filters__option--native --active">
                <GenericMultiSelect
                  name="inspection_type_id"
                  value={state.filters.inspectionTypeId || []}
                  fixedOptions={inspectionTypesForSelect.map((opt) => ({ title: opt.label, value: opt.value }))}
                  label={translate('filters.inspection_type_id', { namespace })}
                  placeholder={`Search ${translate('plural_inspection_type', { namespace: 'filters' })}`}
                  onChange={handleMultiSelectFilterChange('inspectionTypeId')}
                />
              </div>
            )}

            {!!techniciansForSelect.length && (
              <div className="filters__option --active">
                <GenericMultiSelect
                  name="technician"
                  value={state.filters.technician || []}
                  fixedOptions={techniciansForSelect.map((opt) => ({ title: opt.label, value: opt.value }))}
                  label={translate('technician', { namespace: 'filters' })}
                  placeholder={`Search ${translate('plural_technician', { namespace: 'filters' })}`}
                  onChange={handleMultiSelectFilterChange('technician')}
                />
              </div>
            )}

            {!!assetsForSelect.length && (
              <div className="filters__option --active">
                <TreeMultiSelect
                  name="asset"
                  value={state.filters.asset || []}
                  label={translate('filters.asset', { namespace })}
                  placeholder={`Search ${translate('plural_asset', { namespace: 'filters' })}`}
                  options={assetsForSelect}
                  onChange={handleMultiSelectFilterChange('asset')}
                />
              </div>
            )}

            {hasShowIsPublicReportFilter && (
              <div className="filters__option--native --active">
                <BooleanSelect
                  name="is_public_report"
                  value={state.filters.isPublicReport}
                  label={translate('filters.is_public_report', { namespace })}
                  placeholder={I18n.t('generic.all')}
                  selectOptions={{
                    id: 'is-public-report',
                    onChange: onChangeCommonFilter('isPublicReport')
                  }}
                />
              </div>
            )}
          </fieldset>
          <fieldset className="filters__actions">
            <div role="button" tabIndex="-3" className="qmb-control--sm--reset" onClick={onResetFilters}>
              <i className="fa-light fa-arrow-rotate-left" />
              {I18n.t('generic.reset')}
            </div>
            {!isProduction && (
              <div role="button" tabIndex="-4" className="qmb-control--sm--reset" onClick={onResetCache}>
                {I18n.t('generic.reset_cache')}
              </div>
            )}
          </fieldset>
        </article>
      )}

      {!hasDisableAllFilters && !hasDisableAllBulkActions && <hr className="workspace__split" />}

      {!hasDisableAllBulkActions && (
        <div className="qmb-content-actions">
          <div className="content-actions__group">
            <div
              role="button"
              tabIndex="0"
              className="qmb-control js-bulk-action"
              onClick={onBulkDownload}
              data-js-download-selected
              disabled>
              <i className="fa-light fa-circle-down" />
              {translate('table.buttons.download', { namespace })}
            </div>

            {!hasShowAllReportsInCustomerPortal && (
              <div role="button" tabIndex="-1" className="qmb-control js-bulk-action" onClick={onBulkPublish} disabled>
                <i className="fa-light fa-share-from-square" />
                {translate('table.buttons.publish_to_customer_portal', { namespace })}
              </div>
            )}

            {canDeleteReports && (
              <div 
                role="button" 
                tabIndex="-2" 
                className="qmb-control js-bulk-action" 
                onClick={onBulkDelete} 
                data-js-bulk-delete
                disabled
              >
                <i className="fa-light fa-trash-can" />
                {translate('table.buttons.delete', { namespace })}
              </div>
            )}
          </div>
        </div>
      )}

      <CustomScrollbar>
        <article className="--scroll-x --disable-back-gesture">
          <div ref={tableRef} id="inspection-reports-grid" data-src-url={reportsUrl} className="qmb-table" />
        </article>
      </CustomScrollbar>
    </div>
  );
}

IndexTable.propTypes = {
  reportsUrl: PropTypes.string.isRequired,
  deleteReportsUrl: PropTypes.string.isRequired,
  markReportsPublicUrl: PropTypes.string.isRequired,
  defaultHiddenColumns: PropTypes.arrayOf(PropTypes.string),
  nonHidableColumns: PropTypes.arrayOf(PropTypes.string),
  namespace: PropTypes.string,
  buildingsForSelect: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string,
      value: PropTypes.any,
      data: PropTypes.object
    })
  ),
  buildingStatusesForSelect: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string,
      value: PropTypes.any,
      data: PropTypes.object
    })
  ),
  territoriesForSelect: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string,
      value: PropTypes.any,
      data: PropTypes.object
    })
  ),
  inspectionTypesForSelect: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string,
      value: PropTypes.any,
      data: PropTypes.object
    })
  ),
  techniciansForSelect: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string,
      value: PropTypes.any
    })
  ),
  assetsForSelect: PropTypes.arrayOf(PropTypes.object),
  hasShowAllReportsInCustomerPortal: PropTypes.bool,
  canDeleteReports: PropTypes.bool,
  cacheKey: PropTypes.string,
  hasShowInspectionDateFilter: PropTypes.bool,
  hasShowIsPublicReportFilter: PropTypes.bool,
  hasDisableAllFilters: PropTypes.bool,
  hasDisableAllBulkActions: PropTypes.bool,
  hasIntegrationStatus: PropTypes.bool,
  canSendEmailInApp: PropTypes.bool,
  pageSize: PropTypes.number
};

IndexTable.defaultProps = {
  namespace: DEFAULT_NAMESPACE,
  buildingsForSelect: [],
  buildingStatusesForSelect: [],
  territoriesForSelect: [],
  inspectionTypesForSelect: [],
  techniciansForSelect: [],
  assetsForSelect: [],
  hasShowAllReportsInCustomerPortal: false,
  canDeleteReports: false,
  cacheKey: DEFAULT_CACHE_KEY,
  defaultHiddenColumns: [],
  nonHidableColumns: DEFAULT_NON_HIDABLE_COLUMNS,
  hasShowInspectionDateFilter: true,
  hasShowIsPublicReportFilter: true,
  hasDisableAllFilters: false,
  hasDisableAllBulkActions: false,
  hasIntegrationStatus: false,
  canSendEmailInApp: false,
  pageSize: DEFAULT_PAGE_SIZE
};

export default IndexTable;

/* eslint-enable react/no-unescaped-entities */
